import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  ClipboardList,
  MessageCircle,
  Ruler,
  ShieldCheck,
  Timer,
  Truck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { SafeImage } from "@/components/SafeImage";

import { APPLICATIONS, COMPANY, IMAGES, SEO } from "@/mock";
import { SectionTitle, StatPill } from "@/pages/_bits";
import { Container, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export function ApplicationsPage() {
  useEffect(() => setTitle("Applications"), []);

  const [showAllCities, setShowAllCities] = useState(false);
  const CITY_COLLAPSE_COUNT = 12;

  const visibleCities = showAllCities
    ? SEO.majorCities
    : SEO.majorCities.slice(0, CITY_COLLAPSE_COUNT);

  return (
    <div>
      <section className="py-14">
        <Container>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Applications / Industries Served</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">
            National Wire Products supplies to contractors and infrastructure teams across Assam and Northeast India.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {APPLICATIONS.map((a) => (
              <Card key={a.title} className="border-border/60 bg-background/70">
                <CardHeader>
                  <CardTitle className="text-base font-extrabold tracking-tight">{a.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{a.desc}</CardContent>
              </Card>
            ))}
          </div>

          {/* Regional coverage (SEO reinforcement without keyword stuffing) */}
          <div className="mt-10 rounded-3xl border border-border/60 bg-secondary/25 p-6">
            <SectionTitle
              eyebrow="Coverage"
              title="Supply coverage across Northeast India"
              desc="We support contractors, EPCs, distributors and government supply requirements across the Northeast region. Share your site location and quantity—our team will confirm lead time and dispatch plan."
            />

            <div className="mt-6 grid gap-6 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="text-sm font-extrabold tracking-tight">States</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SEO.northeastStates.map((s) => (
                    <Badge key={s} className="bg-emerald-100 text-emerald-950 border-emerald-200">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-extrabold tracking-tight">Major cities</div>

                  {/* Toggle: keeps content crawlable (cities are still in DOM via hidden list below) */}
                  <button
                    type="button"
                    onClick={() => setShowAllCities((v) => !v)}
                    className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/70 px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
                    aria-expanded={showAllCities}
                  >
                    {showAllCities ? "Show less" : "Show more"}
                    {showAllCities ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {visibleCities.map((c) => (
                    <Badge key={c} className="bg-amber-100 text-amber-950 border-amber-200">
                      {c}
                    </Badge>
                  ))}
                </div>

                {/* Hidden full list for SEO/crawling (non-intrusive) */}
                <div className="sr-only">
                  {SEO.majorCities.join(", ")}
                </div>

                <p className="mt-4 text-sm text-muted-foreground">
                  This list represents common procurement hubs in the region—delivery feasibility depends on product type, quantity and transport availability.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 rounded-3xl border border-border/60 bg-slate-950 p-8 text-slate-50">
            <div className="text-xs font-bold tracking-widest text-emerald-200 uppercase">Tell us your application</div>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight">We’ll recommend the right configuration</h2>
            <p className="mt-2 text-sm text-slate-200 max-w-2xl">
              Share location, quantity and site environment (rainfall/corrosion/security). We’ll suggest coating, mesh and packaging options.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400" asChild>
                <Link to="/request-quote">
                  Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                onClick={() =>
                  window.open(
                    makeWhatsAppUrl(
                      COMPANY.whatsapp,
                      "Hello, please recommend the right product configuration for my site."
                    ),
                    "_blank"
                  )
                }
              >
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export function QualityPage() {
  useEffect(() => setTitle("Quality, Trust & Certifications"), []);

  const points = [
    {
      icon: BadgeCheck,
      title: "ISO 9001:2015 certified process",
      desc: "Documentation-driven manufacturing and inspection discipline.",
    },
    {
      icon: ShieldCheck,
      title: "Premium raw materials",
      desc: "Better consistency, coating adherence and overall durability.",
    },
    {
      icon: Ruler,
      title: "Custom sizes and specifications",
      desc: "Align supply to drawings, BOQ and site needs.",
    },
    {
      icon: Truck,
      title: "Timely delivery across Northeast India",
      desc: "Practical dispatch planning for contractors and distributors.",
    },
  ];

  return (
    <div>
      <section className="py-14">
        <Container>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Quality, Trust & Certifications</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">
            When you’re supplying to infrastructure sites and contractors, consistency and documentation matter. This page covers our quality approach, trust factors and ISO certification.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {points.map((p) => (
              <Card key={p.title} className="border-border/60 bg-background/70">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-base font-extrabold tracking-tight">
                    <span className="rounded-xl bg-emerald-50 p-2 border border-emerald-100">
                      <p.icon className="h-5 w-5 text-emerald-900" />
                    </span>
                    {p.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{p.desc}</CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-border/60 bg-secondary/25 p-8">
            <div className="text-sm font-extrabold tracking-tight">What buyers like about working with us</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <StatPill icon={Timer} title="Clear lead times" desc="No surprises for site planning" />
              <StatPill icon={ClipboardList} title="Procurement-ready RFQ" desc="Fast quote with spec alignment" />
              <StatPill icon={Truck} title="Repeat supply" desc="For ongoing projects & distributors" />
            </div>
          </div>

          <QualityCertificationsSection />
        </Container>
      </section>
    </div>
  );
}

function QualityCertificationsSection() {
  const navigate = useNavigate();

  const checks = [
    "Incoming material inspection",
    "Coating thickness checks (as applicable)",
    "Diameter & dimensional verification",
    "Tensile / strength verification (as applicable)",
    "Packaging and dispatch checks",
  ];

  return (
    <div className="mt-12">
      <div className="rounded-3xl border border-border/60 bg-secondary/25 p-6 sm:p-8">
        <div className="text-xs font-bold tracking-widest text-emerald-700 uppercase">Certifications & quality assurance</div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight">Inspection discipline for project-ready supply</h2>
        <p className="mt-3 text-base text-muted-foreground max-w-3xl">
          Quality matters when your project timelines and safety depend on it. We follow an ISO-aligned process and support IS/ASTM compliance as applicable.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
              <Card className="border-border/60 bg-background/70">
                <CardHeader>
                  <CardTitle className="text-lg font-extrabold tracking-tight">{COMPANY.certification}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Our manufacturing and inspection workflow is designed to maintain consistency and traceability. For project supply, share your standard requirement in RFQ to confirm exact compliance.
                </CardContent>
              </Card>

              <div className="mt-6 rounded-3xl border border-border/60 bg-secondary/25 p-6">
                <div className="text-sm font-extrabold tracking-tight">Typical quality checks</div>
                <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
                  {checks.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-3xl border border-border/60 bg-slate-950 p-6 text-slate-50">
                <div className="text-xs font-bold tracking-widest text-emerald-200 uppercase">For tenders / govt supply</div>
                <div className="mt-2 text-xl font-extrabold tracking-tight">Need testing and documentation?</div>
                <p className="mt-2 text-sm text-slate-200">
                  Share the tender clause / spec sheet. We’ll confirm feasible documentation, test requirements and delivery schedule.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button
                    className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                    onClick={() => navigate("/request-quote")}
                  >
                    Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                    onClick={() =>
                      window.open(
                        makeWhatsAppUrl(
                          COMPANY.whatsapp,
                          "Hello, I need documentation/testing confirmation for a tender."
                        ),
                        "_blank"
                      )
                    }
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                  </Button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl border border-border/60 bg-background/70">
                <SafeImage
                  src={IMAGES.quality}
                  alt="Quality assurance"
                  imgClassName="h-[420px] w-full object-cover"
                  fallbackLabel="Quality assurance"
                />
              </div>

              <div className="mt-6 rounded-3xl border border-border/60 bg-secondary/25 p-6">
                <div className="text-sm font-extrabold tracking-tight">Documentation support</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Purchase orders and tender supply often require specific test reports. Share your clause list and we’ll confirm what’s feasible.
                </p>
                <Separator className="my-4" />
                <div className="text-sm text-muted-foreground">
                  Note: Specifications on the website are typical ranges and should be confirmed in RFQ.
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}
